import { useState } from "react";
import { LuChevronDown, LuPhone } from "react-icons/lu";

import { business, faqs } from "../../content/site";

/**
 * FAQ accordion.
 *
 * Accessibility model (WAI-ARIA disclosure pattern, one disclosure per
 * question rather than an exclusive accordion):
 *  - Each question is a real <button> wrapped in a heading element, so screen
 *    reader users can jump question to question with heading navigation.
 *  - The button carries aria-expanded and aria-controls.
 *  - The panel carries the matching id, role="region" and aria-labelledby
 *    pointing back at the button.
 *  - Closed panels use the `hidden` ATTRIBUTE, so they are removed from the
 *    accessibility tree and from the tab order — not merely collapsed to zero
 *    height, which would leave focusable content reachable but invisible.
 *
 * Multiple panels may be open at once. That is friendlier than an exclusive
 * accordion: opening a second question never silently closes the answer the
 * visitor was still reading, and it removes a surprise layout jump.
 *
 * TODO (SEO): add FAQPage JSON-LD structured data once this copy is final and
 * approved by the owner. The schema MUST mirror exactly what is rendered here —
 * same questions, same answers, word for word. Google treats schema that does
 * not match visible page content as a violation, so generate it from the same
 * `faqs` array in src/content/site.js rather than writing it by hand.
 */
const FAQ = () => {
  // Set of open question indexes. Empty on load: every answer is one click away
  // and nothing is hidden behind a scroll.
  const [openItems, setOpenItems] = useState(() => new Set());

  const toggle = (index) => {
    setOpenItems((previous) => {
      const next = new Set(previous);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  if (!faqs || faqs.length === 0) {
    return null;
  }

  return (
    <section
      id="faq"
      aria-labelledby="faq-heading"
      className="section bg-surface"
    >
      <div className="container-page">
        <header className="max-w-prose">
          <p className="kicker">Before you call</p>
          <h2 id="faq-heading" className="mt-3 text-display text-ink">
            Questions we get asked most
          </h2>
          <p className="mt-4 text-body-lg text-ink-muted">
            Straight answers to the things people ask on the phone, so you know
            what to expect before anyone is on the way.
          </p>
        </header>

        <div className="mt-10 max-w-prose divide-y divide-line border-y border-line">
          {faqs.map((item, index) => {
            const isOpen = openItems.has(index);
            const buttonId = `faq-question-${index}`;
            const panelId = `faq-answer-${index}`;

            return (
              <div key={item.q}>
                <h3>
                  <button
                    type="button"
                    id={buttonId}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => toggle(index)}
                    className="flex w-full min-h-tap items-center justify-between gap-4
                               py-4 text-left text-body-lg font-semibold text-ink
                               hover:text-ink-brand active:text-ink-brand
                               aria-expanded:text-ink-brand
                               transition-colors duration-base ease-standard"
                  >
                    <span className="min-w-0 [overflow-wrap:anywhere]">
                      {item.q}
                    </span>
                    <LuChevronDown
                      aria-hidden="true"
                      className={`h-5 w-5 shrink-0 text-ink-subtle
                                  transition-transform duration-base ease-standard
                                  ${isOpen ? "rotate-180" : "rotate-0"}`}
                    />
                  </button>
                </h3>

                {/* No display utility class on this element: the `hidden`
                    attribute below is what controls visibility, and a
                    Tailwind display class would override it. */}
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={buttonId}
                  hidden={!isOpen}
                  className="pb-5 pr-2"
                >
                  <p className="max-w-prose text-body text-ink-muted">
                    {item.a}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-8 max-w-prose text-body text-ink-muted">
          Still not covered here? Call and ask.{" "}
          <a
            href={business.phone.href}
            className="text-link relative inline-flex min-h-tap items-center gap-1 py-2 font-semibold"
          >
            <LuPhone aria-hidden="true" className="h-4 w-4 shrink-0" />
            <span className="tabular">{business.phone.display}</span>
          </a>
        </p>
      </div>
    </section>
  );
};

export default FAQ;
